import { apiClient } from '@/api/client';
import type { ApiResponseWrapper } from '@/types/api.types';

export type ProbationBucket = 'upcoming' | 'due' | 'overdue' | 'extended' | 'completed';

export type ProbationStatus =
  | 'open'
  | 'review_submitted'
  | 'extended'
  | 'confirmed'
  | 'ended'
  | 'cancelled';

export type ProbationRecommendation = 'confirm' | 'extend' | 'end';

export interface ProbationPolicy {
  companyId: string;
  durationDays: number;
  /** Days before the end date on which reminders go out, e.g. `[14, 7, 1]`. */
  reminderDaysBefore: number[];
  escalateAfterDaysOverdue: number;
  maxExtensions: number;
  /** Longest single extension, measured from the current end date. */
  maxExtensionDays: number;
  reviewRequired: boolean;
  /** True while the company has never saved its own policy. */
  isDefault: boolean;
  updatedAt?: string;
}

export type ProbationPolicyInput = Pick<
  ProbationPolicy,
  | 'durationDays'
  | 'reminderDaysBefore'
  | 'escalateAfterDaysOverdue'
  | 'maxExtensions'
  | 'maxExtensionDays'
  | 'reviewRequired'
>;

export interface ProbationReview {
  reviewerUserId: string;
  submittedAt: string;
  rating: number;
  strengths: string;
  improvements: string;
  recommendation: ProbationRecommendation;
}

export interface ProbationExtension {
  previousEndDate: string;
  newEndDate: string;
  reason: string;
  decidedBy: string;
  decidedAt: string;
}

export interface ProbationDecision {
  outcome: 'confirmed' | 'ended';
  decidedBy: string;
  decidedAt: string;
  effectiveDate: string;
  reason?: string;
}

export interface ProbationCase {
  _id: string;
  companyId: string;
  /** Present on cross-company lists. */
  companyName?: string | null;
  userId: string;
  employmentId: string;
  status: ProbationStatus;
  startDate: string;
  originalEndDate: string;
  currentEndDate: string;
  review?: ProbationReview;
  extensions: ProbationExtension[];
  decision?: ProbationDecision;
  /** Policy as it stood when the case opened; the server enforces these limits. */
  policySnapshot?: Partial<ProbationPolicyInput>;
  bucket: ProbationBucket;
  employee: { userId: string; name: string; email: string; avatarUrl?: string };
}

export interface ProbationCasePage {
  data: ProbationCase[];
  total: number;
  page: number;
  limit: number;
}

export type ProbationSummary = Record<ProbationBucket, number>;

const base = (companyId: string): string => `/companies/${companyId}/hr/probation`;

export async function getProbationPolicy(companyId: string): Promise<ProbationPolicy> {
  const { data } = await apiClient.get<ApiResponseWrapper<ProbationPolicy>>(
    `${base(companyId)}/policy`
  );
  return data.data;
}

export async function updateProbationPolicy(
  companyId: string,
  input: ProbationPolicyInput
): Promise<ProbationPolicy> {
  const { data } = await apiClient.put<ApiResponseWrapper<ProbationPolicy>>(
    `${base(companyId)}/policy`,
    input
  );
  return data.data;
}

export async function listProbationCases(
  companyId: string,
  params: { bucket?: ProbationBucket; page?: number; limit?: number } = {}
): Promise<ProbationCasePage> {
  const { data } = await apiClient.get<ApiResponseWrapper<ProbationCasePage>>(
    `${base(companyId)}/cases`,
    { params }
  );
  return data.data;
}

export async function getProbationSummary(companyId: string): Promise<ProbationSummary> {
  const { data } = await apiClient.get<ApiResponseWrapper<ProbationSummary>>(
    `${base(companyId)}/cases/summary`
  );
  return data.data;
}

export async function getProbationCase(
  companyId: string,
  caseId: string
): Promise<ProbationCase> {
  const { data } = await apiClient.get<ApiResponseWrapper<ProbationCase>>(
    `${base(companyId)}/cases/${caseId}`
  );
  return data.data;
}

/**
 * Undecided cases, across the organization, whose subject currently reports to
 * the caller. Managers see them without `hr:probation`; HR's decision and
 * extension reasoning is omitted from this view.
 */
export async function listMyProbationReviews(): Promise<ProbationCase[]> {
  const { data } = await apiClient.get<ApiResponseWrapper<{ data: ProbationCase[] }>>(
    '/hr/probation/reviewing'
  );
  return data.data.data;
}

/** Every command carries a client-generated id so a retried submission is not applied twice. */
interface Idempotent {
  requestId: string;
}

export interface SubmitProbationReviewInput extends Idempotent {
  rating: number;
  strengths: string;
  improvements: string;
  recommendation: ProbationRecommendation;
}

export interface ExtendProbationInput extends Idempotent {
  newEndDate: string;
  reason: string;
}

export interface ConfirmProbationInput extends Idempotent {
  effectiveDate?: string;
  comment?: string;
}

export interface EndProbationInput extends Idempotent {
  reason: string;
  lastWorkingDate: string;
}

async function command<T>(
  companyId: string,
  caseId: string,
  action: 'review' | 'extend' | 'confirm' | 'end',
  body: T
): Promise<ProbationCase> {
  const { data } = await apiClient.post<ApiResponseWrapper<ProbationCase>>(
    `${base(companyId)}/cases/${caseId}/${action}`,
    body
  );
  return data.data;
}

export const submitProbationReview = (
  companyId: string,
  caseId: string,
  input: SubmitProbationReviewInput
): Promise<ProbationCase> => command(companyId, caseId, 'review', input);

export const extendProbation = (
  companyId: string,
  caseId: string,
  input: ExtendProbationInput
): Promise<ProbationCase> => command(companyId, caseId, 'extend', input);

export const confirmProbation = (
  companyId: string,
  caseId: string,
  input: ConfirmProbationInput
): Promise<ProbationCase> => command(companyId, caseId, 'confirm', input);

export const endProbation = (
  companyId: string,
  caseId: string,
  input: EndProbationInput
): Promise<ProbationCase> => command(companyId, caseId, 'end', input);
